import * as React from 'react';
import { useEffect, useState } from 'react';
import { getAgentSummary, getAggregatePremiumData } from 'services/agentDashboard';
import 'react-circular-progressbar/dist/styles.css';
import { AppBar, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import Spacing from 'components/Layout/components/Spacing';
import { LabeledData } from 'components/Input';
import { formatDollar } from 'helpers/formatters';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AggregatePremiumData, AgentSummaryEntry } from '../../../../types/dashboard.types';

interface AgentSummaryProps {
    producerId?: string;
    productLineCode?: string;
}

const AgentSummary: React.FunctionComponent<AgentSummaryProps> = (props) => {
    const { producerId, productLineCode } = { ...props };
    const [agentSummaryDateFetched, setAgentSummaryDateFetched] = useState<boolean>(false);
    const [agentSummaryData, setAgentSummaryData] = useState<Record<string, AgentSummaryEntry>>();
    const [aggPremData, setAggPremData] = useState<Record<string, string>>();
    const [aggPremDataLoading, setAggPremDataLoading] = useState<boolean>(false);
    const [showEarnedPremium, setShowEarnedPremium] = useState<boolean>(false);

    const fetchAgentSummaryData = async () => {
        const agentSummaryFetchList = await getAgentSummary(producerId, productLineCode);
        const agentSummaryDataFetch = agentSummaryFetchList
            .map((agentSummaryFetch) => ({
                ...agentSummaryFetch,
                currentInForceTiv: formatDollar(agentSummaryFetch.currentInForceTiv, 0),
                currentInForcePremium: formatDollar(agentSummaryFetch.currentInForcePremium, 0),
            }))
            .reduce((out, cur, idx) => {
                out['prog' + idx] = cur;
                return out;
            }, {} as Record<string, AgentSummaryEntry>);
        setAggPremData(undefined);
        setAgentSummaryData(agentSummaryDataFetch);
        setAgentSummaryDateFetched(true);
    };

    const fetchAggPremData = async () => {
        let dataProg0 = {} as AggregatePremiumData;
        let dataProg1 = {} as AggregatePremiumData;
        const aggPremDataTemp: Record<string, string> = {};
        setAggPremDataLoading(true);
        if (agentSummaryData?.prog0.programName) {
            dataProg0 = await getAggregatePremiumData(agentSummaryData.prog0.programName, productLineCode);
            aggPremDataTemp[agentSummaryData.prog0.programName] = formatDollar(dataProg0.grossEarnedPremium, 0);
        }
        if (agentSummaryData?.prog1.programName) {
            dataProg1 = await getAggregatePremiumData(agentSummaryData.prog1.programName, productLineCode);
            aggPremDataTemp[agentSummaryData.prog1.programName] = formatDollar(dataProg1.grossEarnedPremium, 0);
        }

        setAggPremData(aggPremDataTemp);
        setAggPremDataLoading(false);
    };

    const toggleEarned = () => {
        if (!aggPremData) {
            fetchAggPremData();
        }
        setShowEarnedPremium(!showEarnedPremium);
    };

    useEffect(() => {
        setShowEarnedPremium(false);
        fetchAgentSummaryData();
    }, [producerId, productLineCode]);

    return (
        <>
            {agentSummaryDateFetched && (
                <>
                    <Grid
                        container
                        spacing={0}
                        direction={'column'}
                        justifyContent={'space-between'}
                        style={{ height: '100%' }}
                    >
                        <Grid>
                            <AppBar position="static" color="default">
                                <Grid container justifyContent={'center'} alignItems={'center'}>
                                    <Grid>
                                        <Typography variant="h4" style={{ margin: 4 }}>
                                            Summary of Activity
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </AppBar>
                            <Spacing height={'24px'} />
                        </Grid>
                        <Grid container style={{ flexGrow: 1 }} direction={'column'} justifyContent={'space-around'}>
                            <Grid container>
                                {agentSummaryData?.prog1 && (
                                    <Grid xs={6}>
                                        <LabeledData
                                            title={`Gross Premium ${agentSummaryData?.prog1.programName}`}
                                            value={agentSummaryData?.prog1.currentInForcePremium}
                                        />
                                    </Grid>
                                )}
                                {agentSummaryData?.prog0 && (
                                    <Grid xs={5}>
                                        <LabeledData
                                            title={`Gross Premium ${agentSummaryData?.prog0.programName}`}
                                            value={agentSummaryData?.prog0.currentInForcePremium}
                                        />
                                    </Grid>
                                )}
                                {agentSummaryData?.prog0 && (
                                    <Grid xs={1}>
                                        <Tooltip title={`${showEarnedPremium ? 'Hide' : 'Show'} Earned Premium`}>
                                            <IconButton size={'small'} onClick={() => toggleEarned()}>
                                                {showEarnedPremium ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                )}
                                <Collapse in={showEarnedPremium} sx={{ width: '100%' }}>
                                    <Grid container xs={12}>
                                        {agentSummaryData?.prog1 && (
                                            <Grid xs={6}>
                                                <LabeledData
                                                    title={`Earned Premium ${agentSummaryData?.prog1.programName}`}
                                                    value={
                                                        aggPremData && aggPremData[agentSummaryData?.prog1.programName]
                                                            ? aggPremData[agentSummaryData?.prog1.programName]
                                                            : 'N/A'
                                                    }
                                                    loading={aggPremDataLoading}
                                                />
                                            </Grid>
                                        )}
                                        {agentSummaryData?.prog0 && (
                                            <Grid xs={6}>
                                                <LabeledData
                                                    title={`Earned Premium ${agentSummaryData?.prog0.programName}`}
                                                    value={
                                                        aggPremData && aggPremData[agentSummaryData?.prog0.programName]
                                                            ? aggPremData[agentSummaryData?.prog0.programName]
                                                            : 'N/A'
                                                    }
                                                    loading={aggPremDataLoading}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Collapse>
                            </Grid>
                            <Grid container>
                                {agentSummaryData?.prog1 && (
                                    <Grid xs={6}>
                                        <LabeledData
                                            title={`${productLineCode === 'COMMERCIAL_PROPERTY' ? 'TIV' : 'Units'} ${
                                                agentSummaryData?.prog1.programName
                                            }`}
                                            value={
                                                productLineCode === 'COMMERCIAL_PROPERTY'
                                                    ? agentSummaryData?.prog1.currentInForceTiv
                                                    : agentSummaryData?.prog1.currentInForceUnits
                                            }
                                        />
                                    </Grid>
                                )}
                                {agentSummaryData?.prog0 && (
                                    <Grid xs={6}>
                                        <LabeledData
                                            title={`${productLineCode === 'COMMERCIAL_PROPERTY' ? 'TIV' : 'Units'} ${
                                                agentSummaryData?.prog0.programName
                                            }`}
                                            value={
                                                productLineCode === 'COMMERCIAL_PROPERTY'
                                                    ? agentSummaryData?.prog0.currentInForceTiv
                                                    : agentSummaryData?.prog0.currentInForceUnits
                                            }
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container>
                                {agentSummaryData?.prog1 && (
                                    <Grid xs={6}>
                                        <LabeledData
                                            title={`Policies ${agentSummaryData?.prog1.programName}`}
                                            value={agentSummaryData?.prog1.currentInForcePolicies}
                                        />
                                    </Grid>
                                )}
                                {agentSummaryData?.prog0 && (
                                    <Grid xs={6}>
                                        <LabeledData
                                            title={`Policies ${agentSummaryData?.prog0.programName}`}
                                            value={agentSummaryData?.prog0.currentInForcePolicies}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            <Grid>
                                <LabeledData
                                    title="Total Submissions Last 12 Months"
                                    value={agentSummaryData?.prog0?.totalSubmissionsOneYear}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </>
            )}
        </>
    );
};

export default AgentSummary;
